import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Popover,
  Typography,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  describeDisabledReason,
  describePreviewOnlyReason,
  getMyVerticalContext,
  getPreviewVertical,
  getVerticalsCatalog,
  setPreviewVertical,
  type VerticalCatalogEntry,
  type VerticalContextResponse,
} from "@services/verticals";
import { facilityService } from "@services/facilityServices";

/**
 * Executive/global-admin vertical preview switcher icon and popout.
 *
 * Renders a small icon in the left navigation that opens a popout listing every
 * vertical from `GET /api/v1/verticals`. Choosing a vertical writes its slug to
 * sessionStorage under `unicomply.previewVertical`; `authenticatedFetch` then
 * forwards that value as `X-Preview-Vertical` on every API call. The preview
 * is transient (sessionStorage, not localStorage) and does NOT mutate the user
 * record on the server.
 *
 * The component is only mounted by the parent for executive or global
 * administrator users.
 */
interface VerticalPreviewSwitcherProps {
  /** When the preview vertical changes the parent (Dashboard) should refresh
   *  data so the new vertical's content shows immediately. */
  onChange?: (newSlug: string | null) => void;
}

export default function VerticalPreviewSwitcher({ onChange }: VerticalPreviewSwitcherProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [catalog, setCatalog] = useState<VerticalCatalogEntry[]>([]);
  const [context, setContext] = useState<VerticalContextResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewSlug, setPreviewSlugState] = useState<string | null>(() => getPreviewVertical());
  const navigate = useNavigate();

  // Lazily fetch the catalog the first time the popout is opened so the icon
  // never blocks initial dashboard render.
  useEffect(() => {
    if (!anchorEl || catalog.length > 0) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([getVerticalsCatalog("USA"), getMyVerticalContext().catch(() => null)])
      .then(([catalogRes, ctxRes]) => {
        if (cancelled) return;
        setCatalog(catalogRes.verticals || []);
        setContext(ctxRes);
      })
      .catch((err) => {
        // Network failures should not crash the icon; just log so the user can retry.
        console.warn("VerticalPreviewSwitcher: failed to load catalog", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [anchorEl, catalog.length]);

  /** Slug currently used by the backend: preview overrides everything else. */
  const activeSlug = useMemo(() => {
    if (previewSlug) return previewSlug;
    return context?.effective_vertical || context?.user_vertical || "healthcare";
  }, [previewSlug, context]);

  /**
   * Handle a vertical pick:
   *   1. If the user already has at least one facility for that vertical, set
   *      the preview slug, broadcast, and route to Facility Analysis with the
   *      matching facility selected.
   *   2. If the user has zero matching facilities, still set the preview slug
   *      (so X-Preview-Vertical flows on subsequent calls) but also route to
   *      FacilityValidationScreen seeded with the picked vertical so the user
   *      can add one through the standard flow.
   *
   * Both paths use the existing `unicomply:preview-vertical-changed` event so
   * the dropdown filter / compliance refetch happens immediately even when no
   * navigation occurs.
   */
  const handleSelect = async (slug: string) => {
    // Selecting the user's own vertical clears the preview so the header is
    // dropped entirely on subsequent requests.
    const isOwnVertical = context?.user_vertical && context.user_vertical === slug;
    const next = isOwnVertical ? null : slug;

    setPreviewVertical(next);
    setPreviewSlugState(next);
    setAnchorEl(null);
    if (onChange) onChange(next);
    try {
      window.dispatchEvent(
        new CustomEvent("unicomply:preview-vertical-changed", { detail: { slug: next } }),
      );
    } catch {
      // Event constructors are universally supported in modern browsers; ignore old ones.
    }

    // The vertical the user just chose is the slug they want facilities for.
    // `next` may be null when choosing their own vertical because that clears
    // the preview header, but routing still targets the clicked slug.
    const targetVertical = slug;

    try {
      const facilities = await facilityService.getMyFacilities({ vertical: targetVertical });
      const matchingFacility = facilities.find(
        (f) => (f.vertical || "healthcare") === targetVertical,
      );
      if (matchingFacility) {
        navigate("/dashboard/facilities", {
          state: {
            currentView: "MyFacility",
            activeTab: "facilities",
            facilityId: matchingFacility.facility_id,
            facilityName: matchingFacility.facility_name,
            facilityCity: matchingFacility.city,
          },
        });
        return;
      }
      // No facility for this vertical — route to the standard add-facility
      // flow with the picked vertical seeded into router state. Spaces.tsx
      // forwards `preselectedVertical` to FacilityValidationScreen.
      navigate("/dashboard/MyFacility", {
        state: {
          currentView: "facilityValidation",
          preselectedVertical: targetVertical,
        },
      });
    } catch (err) {
      // Failing to fetch facilities should not block the preview itself; the
      // dropdown filter will still apply once it loads in MyFacilityDetails.
      console.warn(
        "VerticalPreviewSwitcher: failed to detect facility for vertical",
        err,
      );
    }
  };

  const handleClear = () => {
    setPreviewVertical(null);
    setPreviewSlugState(null);
    setAnchorEl(null);
    if (onChange) onChange(null);
    try {
      window.dispatchEvent(new CustomEvent("unicomply:preview-vertical-changed", { detail: { slug: null } }));
    } catch {
      // ignore
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={previewSlug ? `Previewing: ${previewSlug}` : "Select an Industry/Vertical"} placement="right">
        <Box
          onClick={(e: React.MouseEvent<HTMLDivElement>) => setAnchorEl(e.currentTarget)}
          sx={{
            display: "flex",
            padding: "10px 8px",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            alignSelf: "stretch",
            cursor: "pointer",
            mb: 2,
            position: "relative",
          }}
          aria-label="Preview vertical"
          data-testid="vertical-preview-switcher-icon"
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "10px",
              border: previewSlug ? "1.5px solid #c2410c" : "1px solid #5bbfd6",
              background: previewSlug
                ? "linear-gradient(145deg, #fff7ed 0%, #fed7aa 100%)"
                : "linear-gradient(145deg, #f0fbfd 0%, #bff2ff 100%)",
              boxShadow: previewSlug
                ? "0 4px 10px rgba(194, 65, 12, 0.18), inset 0 1px 0 rgba(255,255,255,0.75)"
                : "0 4px 10px rgba(10, 130, 157, 0.18), inset 0 1px 0 rgba(255,255,255,0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              fontWeight: 800,
              letterSpacing: "-0.04em",
              color: previewSlug ? "#c2410c" : "#065B70",
              fontFamily: "'Inter', sans-serif",
              userSelect: "none",
              textShadow: "0 1px 0 rgba(255,255,255,0.7)",
            }}
          >
            V
          </Box>
        </Box>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            ml: "8px",
            p: 1.5,
            width: 280,
            borderRadius: "10px",
            border: "1px solid #5bbfd6",
            boxShadow: "0 6px 18px 0 rgba(111, 211, 232, 0.25)",
            backgroundColor: "#FFF",
          },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#065B70", mb: 0.5 }}>
          Select an Industry/Vertical
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#737373", mb: 1 }}>
          Switches what the UI shows without changing your account.
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2, justifyContent: "center" }}>
            <CircularProgress size={14} />
            <Typography sx={{ fontSize: 12, color: "#6b7280" }}>Loading verticals…</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {catalog.map((v) => {
              const isSelected = activeSlug === v.slug;
              const isDisabled = !v.enabled;
              // Live = aggregator-backed rollout (server clears preview_only_reason).
              // Coming soon = force-enabled for preview while content still pending.
              const isLive = !isDisabled && !v.preview_only_reason;
              const showComingSoonBadge =
                Boolean(v.preview_only_reason) || isDisabled;
              const tip = isDisabled
                ? describeDisabledReason(v.disabled_reason)
                : showComingSoonBadge && v.preview_only_reason
                ? describePreviewOnlyReason(v.preview_only_reason)
                : v.description;
              return (
                <Tooltip key={v.slug} title={tip || ""} placement="right" disableHoverListener={!tip}>
                  <Box
                    onClick={() => {
                      if (isDisabled) return;
                      handleSelect(v.slug);
                    }}
                    role="button"
                    aria-pressed={isSelected}
                    aria-disabled={isDisabled}
                    data-testid={`vertical-preview-row-${v.slug}`}
                    data-coming-soon={showComingSoonBadge ? "true" : "false"}
                    data-live={isLive ? "true" : "false"}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: "6px",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.55 : 1,
                      backgroundColor: isSelected ? "#E0F2F7" : "transparent",
                      border: isSelected ? "1px solid #0a829d" : "1px solid transparent",
                      "&:hover": isDisabled ? undefined : { backgroundColor: "#F0FBFD" },
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                        {v.label}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>
                        {v.lookup_mode}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                      {isLive && (
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: "#0a829d",
                            backgroundColor: "#E0F2F7",
                            border: "1px solid #7dd3e8",
                            padding: "1px 4px",
                            borderRadius: "3px",
                            lineHeight: 1.2,
                            letterSpacing: 0.4,
                          }}
                        >
                          ACTIVE
                        </Typography>
                      )}
                      {showComingSoonBadge && (
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: "#c2410c",
                            backgroundColor: "#fff7ed",
                            border: "1px solid #fed7aa",
                            padding: "1px 4px",
                            borderRadius: "3px",
                            lineHeight: 1.2,
                            letterSpacing: 0.4,
                          }}
                        >
                          COMING SOON
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
        {previewSlug && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box
              onClick={handleClear}
              role="button"
              sx={{
                cursor: "pointer",
                px: 1,
                py: 0.5,
                fontSize: 12,
                color: "#c2410c",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#fff7ed", borderRadius: "4px" },
              }}
            >
              Clear preview (return to my vertical)
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}
